import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Plane } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userName, setUserName] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for logged-in user on every route change
  useEffect(() => {
    const name = localStorage.getItem('qsmd_client_name');
    setUserName(name || null);
  }, [location.pathname]);

  const isLoggedIn = !!userName;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 animate-fade-in-down ${isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-sm py-2'
        : 'bg-transparent py-4'
        }`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-14' : 'h-16 md:h-20'
          }`}>
          {/* Logo */}
          <a
            href="/"
            data-testid="logo"
            className="flex items-center gap-2 group"
          >
            <div className={`rounded-xl flex items-center justify-center transition-all duration-300 ${isScrolled ? 'w-8 h-8 bg-blue-600' : 'w-10 h-10 bg-white/20 backdrop-blur-sm'
              }`}>
              <Plane className={`text-white transition-all duration-300 ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />
            </div>
            <span className={`font-bold transition-all duration-300 ${isScrolled ? 'text-slate-900 text-base' : 'text-white text-lg'
              }`}>
              Quais São Meus Direitos
            </span>
          </a>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isLoggedIn ? (
              <button
                onClick={() => navigate('/painel/home')}
                className={`font-bold flex items-center gap-2.5 transition-all duration-300 group ${isScrolled ? 'text-slate-800 hover:text-blue-600' : 'text-white hover:text-blue-200'
                  }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm transition-all ${isScrolled ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200' : 'bg-white/20 text-white group-hover:bg-white/30'
                  }`}>
                  {userName.charAt(0).toUpperCase()}
                </div>
                <span className="hidden lg:inline">{userName}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={`font-extrabold flex items-center gap-1.5 transition-all duration-300 ${isScrolled ? 'text-slate-800 hover:text-blue-600' : 'text-white hover:text-blue-200'
                  }`}
              >
                Entrar <span className="text-xl leading-none">➤</span>
              </button>
            )}
            <Button
              data-testid="header-cta"
              onClick={() => navigate('/elegibilidade/voos')}
              className={`rounded-full font-bold transition-all duration-300 ${isScrolled
                ? 'bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm h-auto'
                : 'bg-yellow-400 hover:bg-yellow-300 text-blue-900 px-6'
                }`}
            >
              Verifique GRÁTIS
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {isLoggedIn && (
              <button
                onClick={() => navigate('/painel/home')}
                className={`w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm transition-all ${isScrolled ? 'bg-blue-100 text-blue-600' : 'bg-white/20 text-white'
                  }`}
              >
                {userName.charAt(0).toUpperCase()}
              </button>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 transition-colors ${isScrolled ? 'text-slate-700' : 'text-white'
                }`}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white shadow-lg border-t border-slate-100"
        >
          <div className="px-4 py-6 space-y-4">
            {isLoggedIn ? (
              <button
                onClick={() => { navigate('/painel/home'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-4 bg-blue-50 text-blue-700 font-bold rounded-xl transition-colors border border-blue-100"
              >
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-extrabold text-lg">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="text-base font-bold text-slate-900">Olá, {userName}!</p>
                  <p className="text-xs text-blue-600 font-medium">Ver meu painel</p>
                </div>
              </button>
            ) : (
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="flex items-center justify-between w-full px-4 py-4 bg-slate-50 text-slate-800 font-bold hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
              >
                <span className="text-lg">Entrar</span>
                <span className="text-2xl">➤</span>
              </button>
            )}

            <Button
              onClick={() => { navigate('/elegibilidade/voos'); setMobileMenuOpen(false); }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold py-6 text-lg shadow-blue-600/20 shadow-xl"
            >
              Verifique GRÁTIS
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
